import i18n from 'i18next';

import phoneFormatter from 'phone-formatter';
import { MAX_DISPLAY_NAME_LENGTH } from '@rwc/constants/welcome';
import { formatDate } from '@ringcentral/rwc-utils/src/date-time';

import { makeOverridableFunction } from '@ringcentral/rwc-utils/src/override-api';

import { WITH_FORMATTED_PARTICIPANTS_COUNT } from '@ringcentral/rwc-bridge';

/**
 * Cuts displayName to the allowed number of characters
 * @param {string} displayName
 * @return {string}
 */
export const cutDisplayName = displayName =>
    displayName && typeof displayName === 'string'
        ? displayName.substr(0, MAX_DISPLAY_NAME_LENGTH)
        : displayName;

/**
 * Strips spaces and dashes from numeric meeting ID
 * @param {string} value
 * @returns {string}
 */
export const stripMeetingId = value => {
    const tValue = value.trim();

    return tValue.match(/^[\d\s-]+$/) ? tValue.replace(/[\s-]/g, '') : value;
};

export const getMeetingName = (scheduleEvent, bridgeInfo) => {
    if (bridgeInfo && bridgeInfo.name && bridgeInfo.name.trim()) {
        return bridgeInfo.name;
    }

    if (scheduleEvent && scheduleEvent.title && scheduleEvent.title.trim()) {
        return scheduleEvent.title;
    }

    return i18n.t('global.DEFAULT_MEETING_NAME');
};

const getDate = ({ days, hours, minutes, seconds }) =>
    [
        days ? i18n.t('global.duration.DAYS', { days }) : '',
        hours ? i18n.t('global.duration.HOURS', { hours }) : '',
        minutes ? i18n.t('global.duration.MINUTES', { minutes }) : '',
        seconds ? i18n.t('global.duration.SECONDS', { seconds: seconds }) : '',
    ].filter(Boolean);

export const formatDuration = sec => {
    const parsedSec = parseInt(sec, 10);
    if (isNaN(parsedSec)) return sec;

    const totalSec = Math.ceil(parsedSec);
    const days = Math.floor(totalSec / 86400);
    const hours = Math.floor((totalSec - days * 86400) / 3600);
    const minutes = Math.floor((totalSec - (hours * 3600 + days * 86400)) / 60);
    const seconds = Math.floor(totalSec % 60);

    return getDate({ days, hours, minutes, seconds }).join(' ');
};

export const removeHexFromDeviceLabel = label =>
    String(label).replace(/\s+\([0-9a-f]{4}:[0-9a-f]{4}\)$/, '');

// only for English locale
export const beautifyDeviceLabel = label =>
    removeHexFromDeviceLabel(label).replace(/(Default|Communications?) - /, '');

export default (value, format) => {
    if (value == null) {
        return '';
    }

    if (value instanceof Date) {
        return formatDate({ date: value, locale: i18n.language });
    }

    format = format.split(',');

    if (format[0] === 'date') {
        return formatDate({ date: value, mask: format[1] });
    }

    if (format[0] === 'duration') {
        return formatDuration(value).trim();
    }

    if (format[0] === 'phone') {
        return phoneFormatter.format(value, format[1]).replace(/N/g, '');
    }

    if (format[0] === 'conference') {
        return phoneFormatter
            .format(value, format[1], { normalize: 'no' })
            .replace(/N/g, '')
            .trim();
    }

    return value;
};

const MAX_UNREAD_MSG_COUNT = 99;
const MAX_REACTIONS_COUNT = 99;
const MAX_PARTICIPANTS_COUNT = 999;

export const formatUnreadMsgCount = count => {
    if (!count) return '';

    return count > MAX_UNREAD_MSG_COUNT ? `${MAX_UNREAD_MSG_COUNT}+` : String(count);
};

export const formatReactionsCount = count => {
    if (!count) return '';

    return count > MAX_REACTIONS_COUNT ? `${MAX_REACTIONS_COUNT}+` : String(count);
};

export const formatParticipantsCount = count => {
    if (!count) return 1;

    return count > MAX_PARTICIPANTS_COUNT ? `${MAX_PARTICIPANTS_COUNT}+` : String(count);
};

export const withFormattedUnreadMsgCount = count =>
    `${count ? `'(${formatUnreadMsgCount(count)})'` : ''}`;

export const withFormattedParticipantsCount = makeOverridableFunction(
    count => `'(${count})'`,
    WITH_FORMATTED_PARTICIPANTS_COUNT
);
